import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MAX_AUDIO_RECORDING_DURATION_SECONDS } from '../Utils/audioQueue';

const WAVE_BAR_COUNT = 44;
const MAX_WAVEFORM_HISTORY = 80;
const WAVE_HISTORY_INTERVAL_MS = 140;

const getAudioMimeType = () => {
  if (typeof MediaRecorder === 'undefined') {
    return '';
  }

  const preferredTypes = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];

  return preferredTypes.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const getAudioFileExtension = (mimeType) => {
  if (!mimeType) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('webm')) return 'webm';
  return 'webm';
};

const mergeAudioStreams = (micStream, displayStream) => {
  if (!displayStream || !displayStream.getAudioTracks().length) {
    return { mergedStream: micStream, audioContext: null };
  }

  const AudioContextConstructor =
    typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!AudioContextConstructor) {
    return { mergedStream: micStream, audioContext: null };
  }

  const audioCtx = new AudioContextConstructor();
  const destination = audioCtx.createMediaStreamDestination();
  const micSource = audioCtx.createMediaStreamSource(micStream);
  micSource.connect(destination);
  const displaySource = audioCtx.createMediaStreamSource(displayStream);
  displaySource.connect(destination);

  return { mergedStream: destination.stream, audioContext: audioCtx };
};

export default function useAudioRecorder({
  isMobile = false,
  maxRecordingDurationSeconds = MAX_AUDIO_RECORDING_DURATION_SECONDS,
  onError,
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  const [pendingAudio, setPendingAudio] = useState(null);
  const [isAudioReviewOpen, setIsAudioReviewOpen] = useState(false);
  const [waveformLevel, setWaveformLevel] = useState(0);
  const [waveformBars, setWaveformBars] = useState(() => Array(WAVE_BAR_COUNT).fill(0));
  const [waveformHistory, setWaveformHistory] = useState([]);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const displayStreamRef = useRef(null);
  const waveformAnimationRef = useRef(null);
  const waveformAnalyserRef = useRef(null);
  const waveformContextRef = useRef(null);
  const waveformHistoryIntervalRef = useRef(null);
  const waveformBarsRef = useRef(waveformBars);
  const recordingAutoStopTimerRef = useRef(null);
  const recordingStopReasonRef = useRef('manual');

  const supportsDisplayCapture = useMemo(
    () => typeof window !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia),
    []
  );
  const canUseDisplayAudio = useMemo(() => supportsDisplayCapture && !isMobile, [supportsDisplayCapture, isMobile]);
  const maxRecordingDurationMs = useMemo(
    () => Math.max(1, Number(maxRecordingDurationSeconds) || MAX_AUDIO_RECORDING_DURATION_SECONDS) * 1000,
    [maxRecordingDurationSeconds]
  );

  const clearRecordingAutoStopTimer = useCallback(() => {
    if (recordingAutoStopTimerRef.current) {
      clearTimeout(recordingAutoStopTimerRef.current);
      recordingAutoStopTimerRef.current = null;
    }
  }, []);

  const clearPendingAudio = useCallback(() => {
    setPendingAudio((current) => {
      if (current?.url) {
        URL.revokeObjectURL(current.url);
      }
      return null;
    });
    setIsAudioReviewOpen(false);
  }, []);

  const stopWaveformAnalyzer = useCallback(() => {
    if (waveformAnimationRef.current) {
      cancelAnimationFrame(waveformAnimationRef.current);
      waveformAnimationRef.current = null;
    }
    if (waveformContextRef.current) {
      waveformContextRef.current.close().catch(() => {});
      waveformContextRef.current = null;
    }
    waveformAnalyserRef.current = null;
    setWaveformLevel(0);
    setWaveformBars(Array(WAVE_BAR_COUNT).fill(0));
  }, []);

  const cleanupCapturedAudioSources = useCallback(() => {
    stopWaveformAnalyzer();
    const stopTracks = (stream) => {
      stream?.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {}
      });
    };

    stopTracks(micStreamRef.current);
    stopTracks(displayStreamRef.current);
    micStreamRef.current = null;
    displayStreamRef.current = null;

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, [stopWaveformAnalyzer]);

  const initializeWaveformAnalyzer = useCallback((stream) => {
    if (!stream) return;
    stopWaveformAnalyzer();
    const AudioContextConstructor =
      typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!AudioContextConstructor) return;

    try {
      const context = new AudioContextConstructor();
      const analyser = context.createAnalyser();
      analyser.fftSize = 128;
      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        analyser.getByteTimeDomainData(dataArray);
        const chunkSize = Math.max(1, Math.floor(dataArray.length / WAVE_BAR_COUNT));
        const bars = [];
        for (let i = 0; i < WAVE_BAR_COUNT; i++) {
          const start = i * chunkSize;
          let sum = 0;
          let count = 0;
          for (let j = start; j < start + chunkSize && j < dataArray.length; j++) {
            sum += Math.abs(dataArray[j] - 128);
            count++;
          }
          bars.push(count > 0 ? Math.min(1, (sum / count) / 128) : 0);
        }
        const maxValue = Math.max(...bars);
        setWaveformBars(bars);
        setWaveformLevel(Math.min(1, maxValue));
        waveformAnimationRef.current = requestAnimationFrame(updateLevel);
      };

      waveformContextRef.current = context;
      waveformAnalyserRef.current = analyser;
      updateLevel();
    } catch (error) {}
  }, [stopWaveformAnalyzer]);

  const startRecording = useCallback(async (mode = 'mic') => {
    clearPendingAudio();

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      onError?.('audio_recording_unsupported');
      return;
    }

    let micStream = null;
    let displayStream = null;

    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = micStream;

      const wantsSystemAudio = mode === 'mixed' && canUseDisplayAudio;
      if (wantsSystemAudio && navigator.mediaDevices?.getDisplayMedia) {
        try {
          displayStream = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: false });
        } catch (displayError) {
          try {
            displayStream = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: true });
          } catch (fallbackError) {
            displayStream = null;
            onError?.('system_audio_unavailable');
          }
        }

        if (displayStream) {
          displayStream.getVideoTracks().forEach((track) => track.stop());
          displayStreamRef.current = displayStream;
        }
      } else if (mode === 'mixed' && !wantsSystemAudio) {
        onError?.('system_audio_unavailable');
      }

      const { mergedStream, audioContext } = mergeAudioStreams(micStream, displayStream);
      if (audioContext) {
        audioContextRef.current = audioContext;
      }
      initializeWaveformAnalyzer(mergedStream);

      const mimeType = getAudioMimeType();
      const recorder = new MediaRecorder(mergedStream, mimeType ? { mimeType } : undefined);

      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        cleanupCapturedAudioSources();
        clearRecordingAutoStopTimer();
        setIsRecording(false);
        mediaRecorderRef.current = null;
        onError?.('audio_recording_error');
      };

      recorder.onstop = () => {
        cleanupCapturedAudioSources();
        clearRecordingAutoStopTimer();
        const finalMimeType = mimeType || recorder.mimeType || 'audio/webm';
        const extension = getAudioFileExtension(finalMimeType);
        const filename = `recording-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
        const audioBlob = new Blob(audioChunksRef.current, { type: finalMimeType });
        const audioFile = new File([audioBlob], filename, { type: finalMimeType });

        setIsRecording(false);
        mediaRecorderRef.current = null;

        if (audioBlob.size === 0) {
          onError?.('audio_recording_empty');
          return;
        }

        setPendingAudio({
          blob: audioBlob,
          file: audioFile,
          url: URL.createObjectURL(audioBlob),
          name: filename,
          stopReason: recordingStopReasonRef.current,
        });
        setIsAudioReviewOpen(true);
        recordingStopReasonRef.current = 'manual';
      };

      mediaRecorderRef.current = recorder;
      recordingStopReasonRef.current = 'manual';
      recorder.start();
      clearRecordingAutoStopTimer();
      recordingAutoStopTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current === recorder && recorder.state !== 'inactive') {
          recordingStopReasonRef.current = 'max_duration';
          recorder.stop();
        }
      }, maxRecordingDurationMs);
      setIsRecording(true);
    } catch (error) {
      cleanupCapturedAudioSources();
      clearRecordingAutoStopTimer();
      setIsRecording(false);
      mediaRecorderRef.current = null;
      onError?.('audio_recording_error');
    }
  }, [
    canUseDisplayAudio,
    cleanupCapturedAudioSources,
    clearPendingAudio,
    clearRecordingAutoStopTimer,
    initializeWaveformAnalyzer,
    maxRecordingDurationMs,
    onError,
  ]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recordingStopReasonRef.current = 'manual';
      recorder.stop();
    }
  }, []);

  useEffect(() => {
    waveformBarsRef.current = waveformBars;
  }, [waveformBars]);

  useEffect(() => {
    if (!isRecording) {
      setRecordingElapsed(0);
      return undefined;
    }

    const startTime = Date.now();
    setRecordingElapsed(0);
    const intervalId = setInterval(() => {
      setRecordingElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isRecording]);

  useEffect(() => {
    if (isRecording && !waveformHistoryIntervalRef.current) {
      waveformHistoryIntervalRef.current = setInterval(() => {
        const level = Math.max(...waveformBarsRef.current);
        setWaveformHistory((prev) => {
          const next = [...prev, Math.min(1, Math.max(0, level))];
          if (next.length > MAX_WAVEFORM_HISTORY) {
            next.shift();
          }
          return next;
        });
      }, WAVE_HISTORY_INTERVAL_MS);
    } else if (!isRecording && waveformHistoryIntervalRef.current) {
      clearInterval(waveformHistoryIntervalRef.current);
      waveformHistoryIntervalRef.current = null;
      setWaveformHistory([]);
    }
  }, [isRecording]);

  useEffect(() => () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    clearRecordingAutoStopTimer();
    cleanupCapturedAudioSources();
    clearPendingAudio();
  }, [cleanupCapturedAudioSources, clearPendingAudio, clearRecordingAutoStopTimer]);

  return {
    canUseDisplayAudio,
    clearPendingAudio,
    isAudioReviewOpen,
    isRecording,
    pendingAudio,
    recordingElapsed,
    setIsAudioReviewOpen,
    startRecording,
    stopRecording,
    waveformHistory,
    waveformLevel,
  };
}
